import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { ApiOverviewResource } from '@repo/api-contract';
import client from '../../util/api-client';
import { pushToast, useDispatch } from '../../state';
import { serverError } from '../../components/modals/constants/errors';
import { OverviewStatCard } from './OverviewStatCard';

const RESOURCE_CONFIG: Record<string, { labelKey: string; color: string }> = {
  customer: { labelKey: 'Customers', color: '#2196F3' },
  vendor: { labelKey: 'Vendors', color: '#9C27B0' },
  item: { labelKey: 'Items', color: '#4CAF50' },
  order: { labelKey: 'Orders', color: '#FF9800' },
  invoice: { labelKey: 'Invoices', color: '#00BCD4' },
  'purchase-order': { labelKey: 'Purchase Orders', color: '#E91E63' },
};

const PERIOD_LABEL_KEYS: Record<ApiOverviewResource['period'], string> = {
  day: 'Last day',
  week: 'Last week',
  month: 'Last month',
  year: 'Last year',
};

export default function Reports() {
  const dispatch = useDispatch();
  const { t } = useTranslation([], {
    keyPrefix: 'team.Components.ReportsPage',
  });
  const [overview, setOverview] = useState<ApiOverviewResource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadOverview() {
      setLoading(true);
      try {
        const response = await client.getOverview.query();
        if (!cancelled) {
          setOverview(response);
        }
      } catch (error) {
        if (cancelled) return;
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadOverview();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const cards = useMemo(
    () =>
      overview.map((resource) => {
        const config = RESOURCE_CONFIG[resource.name] ?? {
          labelKey: resource.name,
          color: '#5b6cff',
        };

        return {
          resource,
          config: {
            label: t(config.labelKey, { defaultValue: config.labelKey }),
            color: config.color,
          },
          periodLabel: t(PERIOD_LABEL_KEYS[resource.period], {
            defaultValue: PERIOD_LABEL_KEYS[resource.period],
          }),
        };
      }),
    [overview, t],
  );

  return (
    <Box className="flex flex-col flex-1 mx-10 my-12 md:mx-20">
      <Typography variant="h5" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
        {t('Reports')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('Overview description')}
      </Typography>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 240,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {cards.map(({ resource, config, periodLabel }) => (
            <OverviewStatCard
              key={resource.name}
              resource={resource}
              config={config}
              periodLabel={periodLabel}
              createdSuffix={t('created')}
              chartCountLabel={t('created')}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
